import { Suspense, lazy } from 'react';
import { Navigate, Outlet, useRoutes } from 'react-router-dom';
// auth
import { AuthGuard, RoleBasedGuard, DeviceGuard } from 'src/auth/guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
import DriverViewLayout from 'src/layouts/driver-view/layout';
import DeviceLayout from 'src/layouts/device';
// components
import { LoadingScreen } from 'src/components/loading-screen';
// config
import { useAuthContext } from 'src/auth/hooks/use-auth-context';

import { ROLES } from 'src/constants';
//
import CareersDashboardPage from 'src/pages/careers-dashboard';
import { landingPath } from './landing-path';
import { authRoutes } from './auth';
import { mainRoutes } from './main';
import { partRoutes } from './part';
import { profileReviewRoutes } from './profile-review';
import { homeEdRenewalRoutes } from './home-ed-renewal';
import { homeEdProgramPlanRoutes } from './home-ed-program-plan';
import { homeEdRoutes } from './home-ed';
import { homeEdFacilitatorRoutes } from './home-ed-facilitators';
import { homeEdFamiliesRoutes } from './home-ed-families';
import { enrollmentRoutes } from './enrollment';
import { homeEdRegistrationAdminRoutes } from './home-ed-registration-admin';
import { assessmentRoutes } from './assessments';
import { purchaseOrderRoutes } from './purchase-order';
import { routingRequestRoutes } from './routing-request';
import { busRoutesRoutes } from './bus-routes';
import { crossboundaryRequestRoutes } from './crossboundary-request';
import { studentRoutes } from './student';
import { vehicleRoutes } from './vehicle';
import { workOrderRoutes } from './workorder';
import { careerRoutes } from './career';
import { screenShareRoutes } from './screenshare';
import { deviceAdminRoutes } from './devices';
import { buildingRoutes } from './buildings';
import { employeeProcessingRoutes } from './employee-processing';
import { offCampusRoutes } from './off-campus';
import { studentDashboardRoutes } from './student-dashboard';

// ----------------------------------------------------------------------

// DASHBOARD
const DashboardPage = lazy(() => import('src/pages/dashboard'));
const SchoolDashboardPage = lazy(() => import('src/pages/school-dashboard'));
const BusRouteDriverPage = lazy(() => import('src/pages/bus-route/driver'));
const BusRunsListPage = lazy(() => import('src/pages/bus-runs/list'));
// ----------------------------------------------------------------------

function RoleBasedRedirect() {
  const { user, loading } = useAuthContext();
  if (loading) return null;
  return <Navigate to={landingPath(user?.roles)} replace />;
}

export default function Router() {
  return useRoutes([
    // SET INDEX PAGE WITH SKIP HOME PAGE
    // {
    //   path: '/',
    //   element: <Navigate to={PATH_AFTER_LOGIN} replace />,
    // },

    // ----------------------------------------------------------------------

    // SET INDEX PAGE WITH HOME PAGE
    {
      path: '/',
      element: (
        <AuthGuard>
          <DashboardLayout>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </DashboardLayout>
        </AuthGuard>
      ),
      children: [
        { element: <RoleBasedRedirect />, index: true },
        { path: 'dashboard', element: <DashboardPage /> },
        ...studentRoutes,
        ...profileReviewRoutes,
        ...enrollmentRoutes,
        ...homeEdRegistrationAdminRoutes,
        ...assessmentRoutes,
        ...vehicleRoutes,
        ...partRoutes,
        ...workOrderRoutes,
        ...purchaseOrderRoutes,
        ...routingRequestRoutes,
        ...crossboundaryRequestRoutes,
        ...busRoutesRoutes,
        ...careerRoutes,
        ...screenShareRoutes,
        ...deviceAdminRoutes,
        ...homeEdRoutes,
        ...homeEdRenewalRoutes,
        ...homeEdProgramPlanRoutes,
        ...homeEdFacilitatorRoutes,
        ...homeEdFamiliesRoutes,
        ...buildingRoutes,
        ...employeeProcessingRoutes,
        ...offCampusRoutes,
        ...studentDashboardRoutes,
      ],
    },
    {
      path: '/school-dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout>
            <Suspense fallback={<LoadingScreen />}>
              <RoleBasedGuard
                roles={[
                  ROLES.USER_ROLE_SCHOOL_PRINCIPAL,
                  ROLES.SUPER_ADMIN,
                  ROLES.USER_ROLE_FINANCE_ADMIN,
                ]}
              >
                <SchoolDashboardPage />
              </RoleBasedGuard>
            </Suspense>
          </DashboardLayout>
        </AuthGuard>
      ),
    },
    {
      path: '/bus-routes/:routeId/driver',
      element: (
        <AuthGuard>
          <DriverViewLayout>
            <Suspense fallback={<LoadingScreen />}>
              <RoleBasedGuard
                roles={[
                  ROLES.USER_ROLE_TRANSPORTATION_ADMIN,
                  ROLES.USER_ROLE_TRANSPORTATION_MANAGER,
                  ROLES.SUPER_ADMIN,
                ]}
              >
                <BusRouteDriverPage />
              </RoleBasedGuard>
            </Suspense>
          </DriverViewLayout>
  </AuthGuard>
      ),
    },
      {
      path: '/careers-dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout>
            <Suspense fallback={<LoadingScreen />}>
              <RoleBasedGuard
                roles={[
                  ROLES.USER_ROLE_SCHOOL_PRINCIPAL,
                  ROLES.USER_ROLE_COUNSELOR,
                  ROLES.USER_ROLE_OFF_CAMPUS_COORDINATOR,
                ]}
              >
                <CareersDashboardPage />
              </RoleBasedGuard>
            </Suspense>
          </DashboardLayout>
        </AuthGuard>
      ),
    },

    // Auth routes
    ...authRoutes,

    // Main routes
    ...mainRoutes,

    // Device routes
    {
      path: '/device',
      element: (
        <DeviceGuard>
          <DeviceLayout>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </DeviceLayout>
        </DeviceGuard>
      ),
      children: [
        {
          element: <BusRunsListPage />,
          index: true,
        },
        {
          element: <BusRouteDriverPage />,
          path: 'bus-routes/:routeId/',
        },
      ],
    },

    // No match 404
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
